import { ArrowIcon } from "@images/svgIcons/arrowIcon"
import {
  BoxStaffPermissionsModal,
  BoxStaffPermissionsModalRef,
} from "@modules/boxStaff/components/boxStaffPermissionsModal"
import { displayRole } from "@modules/boxStaff/entities/boxStaffUtils"
import { addStaffYupValidationSchema } from "@modules/boxStaff/formik/boxStaffFormik"
import { BoxStaffAddInput, BoxStaffRole } from "@modules/boxStaff/gateway/boxStaffDto"
import { useAddBoxStaff } from "@modules/boxStaff/hooks/useBoxStaff"
import { word } from "@modules/core/utils/i18n"
import { SearchUserInput } from "@modules/formik/components/searchUserInput"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HUButton } from "@modules/ui/components/huButton"
import { HuPermissions } from "@modules/ui/components/huPermissions"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { useFormik } from "formik"
import { useRef } from "react"
import { useTheme } from "styled-components"

type Props = {
  boxStaffRole: BoxStaffRole
}

export const BoxStaffAddForm: React.FC<Props> = ({ boxStaffRole }) => {
  const theme = useTheme()
  const boxStaffPermissionsModal = useRef<BoxStaffPermissionsModalRef>(null)
  const { addBoxStaff, isPending } = useAddBoxStaff()
  const toast = useToast()
  const { hideSidebar } = useSidebar()

  const formik = useFormik<BoxStaffAddInput>({
    initialValues: {
      userId: undefined,
      userEmail: undefined,
      role: boxStaffRole,
    },
    validationSchema: addStaffYupValidationSchema(),
    validateOnBlur: false,
    validateOnMount: false,
    onSubmit: async () => {
      await addBoxStaff(formik.values)
        .then(() => {
          toast.show({
            severity: "success",
            detail: word("box.staff.addForm.toast.success"),
          })
          hideSidebar()
        })
        .catch((error) => {
          if (error.status === 409) {
            toast.show({
              severity: "error",
              detail: word("box.staff.addForm.toast.errorAlreadyStaffMember"),
            })
            return
          } else {
            toast.show({
              severity: "error",
              detail: word("box.staff.addForm.toast.error"),
            })
          }
        })
    },
  })

  const handleOnSubmit = async () => {
    await formik.submitForm()
  }

  const handleShowPermissions = () => {
    boxStaffPermissionsModal.current?.open()
  }

  return (
    <>
      <div className="flex flex-column gap-5">
        {/* HEADER */}
        <div className="flex flex-column gap-2">
          <HUText fontStyle="TL" color={theme.mainWhite}>
            {`${word("add.box.member.cardsView.add")} ${displayRole(boxStaffRole).toLowerCase()}`}
          </HUText>
          <HUText fontStyle="BM" color={theme.mainWhite}>
            {word(`box.staff.permissions.description.${boxStaffRole}`)}
          </HUText>
        </div>

        <div className="flex flex-column">
          {/* SEARCH STAFF */}
          <SearchUserInput
            label={word("box.staff.addForm.label.name")}
            textDescription={word("box.staff.addForm.label.description")}
            name="userId"
            isRequiredInput={true}
            isInvalid={isFormFieldInValid("userId", formik)}
            setFieldValue={formik.setFieldValue}
            error={getFormErrorMessage("userId", formik)}
            searchDescriptionText={word("boxSessionAddSubscriberModal.dialog.empytemplate.body")}
          />
          {/* INTERNAL NOTE */} {/* TODO ADD TO INPUT */}
          {/* <div className="flex flex-column">
            <HUText fontStyle="TM" color={theme.mainWhite}>
              {word("box.staff.table.dayToDay.internalNote.label")}
            </HUText>
            <div className="flex flex-row gap-2 mt-2 mb-2">
              <WarningIcon color={theme.neutralColor900} width={20} height={20} />
              <HUText fontStyle="BS" color={theme.neutralColor400}>
                {word("box.staff.table.dayToDay.internalNote.warning")}
              </HUText>
            </div>
            <FormikInput
              flex
              formikStyle={{ flexGrow: 1 }}
              type="textarea"
              placeHolder={word("box.staff.table.dayToDay.internalNote.placeholder")}
              getFieldProps={formik.getFieldProps}
              isInvalid={isFormFieldInValid("note", formik)}
              name="note"
              error={getFormErrorMessage("note", formik)}
            />
          </div> */}
          {/* PERMISSIONS */}
          <HuPermissions
            title="Permissions"
            role={boxStaffRole}
            button={
              <HUButton
                type="Right_Icon"
                size="M"
                colorType="Underline"
                text={word("box.staff.addForm.button.seeDetails")}
                onClick={handleShowPermissions}
                className="ml-auto"
                icon={{
                  iconView: <ArrowIcon color={theme.mainWhite} width={14} height={14} arrowDirection={"right"} />,
                }}
              />
            }
          />
        </div>
        <div>
          {/* {getFormErrorMessage("userEmail", formik)}
          {getFormErrorMessage("userId", formik)} */}
          {/* {formik.errors.userId && <HUText fontStyle="BS" color={theme.errorColor}>{formik.errors.userId}</HUText>}
          {formik.errors.userEmail && <HUText fontStyle="BS" color={theme.errorColor}>{formik.errors.userEmail}</HUText>} */}
        </div>
        {/* BUTTON */}
        <div className=" mb-4 flex flex-row gap-2 align-items-center justify-content-center">
          <HUButton
            type="Left_Icon"
            size="M"
            colorType="Quaternary"
            text={word("global.cancel")}
            onClick={() => hideSidebar()}
            className="w-full"
          />
          <HUButton
            type="Right_Icon"
            size="M"
            colorType="Primary"
            text={`${word("add.box.member.cardsView.add")} ${displayRole(boxStaffRole).toLowerCase()}`}
            onClick={() => handleOnSubmit()}
            disabled={(!formik.values.userEmail && !formik.values.userId) || isPending}
            className="w-full"
          />
        </div>
      </div>
      <BoxStaffPermissionsModal ref={boxStaffPermissionsModal} />
    </>
  )
}
